import type { ModalProps } from 'antd';
import { Modal } from 'antd';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

// Dialog Root - 管理 open 状态
interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | null>(null);

const Dialog: React.FC<DialogProps> = ({
  open = false,
  onOpenChange,
  children,
}) => {
  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  return (
    <DialogContext.Provider value={{ open, onOpenChange: handleOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
};

// DialogTrigger - 触发器（可选）
interface DialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

const DialogTrigger: React.FC<DialogTriggerProps> = ({ children, asChild }) => {
  const context = React.useContext(DialogContext);
  if (!context) return null;

  const handleClick = () => context.onOpenChange(true);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children as React.ReactElement<{ onClick?: () => void }>,
      {
        onClick: handleClick,
      },
    );
  }

  return <span onClick={handleClick}>{children}</span>;
};

// DialogContent - 内容容器
interface DialogContentProps
  extends Omit<ModalProps, 'open' | 'onCancel' | 'footer'> {
  children: React.ReactNode;
  className?: string;
}

const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className,
  ...props
}) => {
  const context = React.useContext(DialogContext);
  if (!context) return null;

  return (
    <Modal
      open={context.open}
      onCancel={() => context.onOpenChange(false)}
      footer={null}
      closeIcon={<X className="h-4 w-4" />}
      centered
      className={cn('dialog-content', className)}
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-lg), inset 0 1px 0 var(--card-highlight)',
        },
        header: {
          backgroundColor: 'transparent',
          marginBottom: '16px',
        },
        body: {
          padding: 0,
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
      {...props}
    >
      {children}
    </Modal>
  );
};

// DialogHeader - 标题区域
interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DialogHeader: React.FC<DialogHeaderProps> = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn('flex flex-col space-y-1.5 text-left', className)}
    {...props}
  >
    {children}
  </div>
);

// DialogFooter - 底部按钮区
interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DialogFooter: React.FC<DialogFooterProps> = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

// DialogTitle - 标题
interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

const DialogTitle: React.FC<DialogTitleProps> = ({
  className,
  children,
  ...props
}) => (
  <h3
    className={cn(
      'text-lg font-semibold leading-none tracking-tight text-text-strong',
      className,
    )}
    {...props}
  >
    {children}
  </h3>
);

// DialogDescription - 描述
interface DialogDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

const DialogDescription: React.FC<DialogDescriptionProps> = ({
  className,
  children,
  ...props
}) => (
  <p className={cn('text-sm text-muted-foreground', className)} {...props}>
    {children}
  </p>
);

// DialogClose - 关闭按钮（可选，用于自定义关闭）
interface DialogCloseProps {
  children: React.ReactNode;
  asChild?: boolean;
}

const DialogClose: React.FC<DialogCloseProps> = ({ children, asChild }) => {
  const context = React.useContext(DialogContext);
  if (!context) return null;

  const handleClick = () => context.onOpenChange(false);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children as React.ReactElement<{ onClick?: () => void }>,
      {
        onClick: handleClick,
      },
    );
  }

  return <span onClick={handleClick}>{children}</span>;
};

// DialogPortal 和 DialogOverlay 不再需要，保留空实现以兼容旧代码
const DialogPortal: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <>{children}</>;
const DialogOverlay: React.FC = () => null;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
